import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";

import urlPatterns from "../../../urls";
import { I18N_NAMESPACES } from "../../../helpers/constants";
import MerchCard from "../MerchCard";
import DashboardMerchCard from "../DashboardMerchCard";
import { AddButtonVariant } from "../MerchCard/style";
import { useDeviceSize } from "../../../helpers/hooks";
import { RewardMerchTrayContent } from "../../organisms/RewardMerchDetailsTray";
import { showErrorAlert } from "../../../helpers/alerts";

export type MerchCardVariant = "dashboard" | "default";

export const DASHBOARD_CARD_VARIANT: MerchCardVariant = "dashboard";
const DEFAULT_CARD_VARIANT: MerchCardVariant = "default";

export interface MerchCardControllerProps {
  name: string;
  pointsCost: number | null;
  cashCost: number | null;
  productSlug: string;
  image: string | null;
  isNew: boolean;
  isLowStock: boolean;
  hasStock: boolean;
  isCollected: boolean;
  unlockLevel?: number;
  memberLevel: number | undefined | null;
  memberPointsBalance: number | undefined | null;
  redeemWithPoints: boolean;
  characteristics: { name: string }[];
  onClick: (event: React.MouseEvent<HTMLElement>, initialContent: RewardMerchTrayContent) => void;
  variant?: MerchCardVariant;
  coverPhotoBackgroundColor: string;
}

const MerchCardController = ({
  productSlug,
  name,
  memberLevel,
  pointsCost,
  cashCost,
  image,
  isNew,
  isLowStock,
  hasStock,
  isCollected,
  unlockLevel,
  memberPointsBalance,
  redeemWithPoints,
  onClick,
  characteristics,
  variant = DEFAULT_CARD_VARIANT,
  coverPhotoBackgroundColor,
}: MerchCardControllerProps) => {
  const { isSmall } = useDeviceSize();

  const { t } = useTranslation([I18N_NAMESPACES.COMMON, I18N_NAMESPACES.REWARDS]);

  /**
   * Shows reward as locked if it is unlocked at a specific level that the current member has not reached yet.
   */
  const cardUnlockLevel = useMemo(() => {
    if (unlockLevel && (!memberLevel || memberLevel < unlockLevel)) {
      return unlockLevel;
    }
    return null;
  }, [unlockLevel, memberLevel]);

  const linkUrl = urlPatterns.REWARD_MERCH_DETAILS_BY_SLUG(productSlug);
  const hasTooFewPoints = (memberPointsBalance ?? 0) < (pointsCost ?? 1) && redeemWithPoints;
  const hasCharacteristics = characteristics.length > 0;

  let addButtonVariant: AddButtonVariant = "standard";
  if (hasCharacteristics) {
    addButtonVariant = "inverted";
  } else if (hasTooFewPoints || !hasStock || !!cardUnlockLevel) {
    addButtonVariant = "disabled";
  }

  const handleRewardMerchCardClick = (event: React.MouseEvent<HTMLElement>) => {
    onClick(event, RewardMerchTrayContent.RewardMerchDetails);
  };

  const handleAddButtonClick = (event: React.MouseEvent<HTMLElement>) => {
    // If the product has characteristics (e.g. Size, Color,...), we need to open the merch tray so user
    // can select the characteristics.
    const startingContent = hasCharacteristics
      ? RewardMerchTrayContent.RewardMerchDetails
      : RewardMerchTrayContent.AddRewardToNextBoxOrCart;
    if (hasCharacteristics) {
      onClick(event, startingContent);
      return;
    }
    if (cardUnlockLevel) {
      showErrorAlert(t("rewards:locked", { unlockLevel }), { autoClose: 10000 });
      return;
    }
    if (hasTooFewPoints) {
      showErrorAlert(t("rewards:notEnoughPoints"), { autoClose: 10000 });
      return;
    }
    if (!hasStock) {
      showErrorAlert(t("common:sorryNoStock"), { autoClose: 10000 });
      return;
    }
    onClick(event, startingContent);
  };

  const cardProps = {
    onAddButtonClick: handleAddButtonClick,
    name,
    linkUrl,
    image: image ?? "",
    isNew,
    // If the product has characteristics (e.g. Size, Color,...), we don't show low stock and not stock
    // indicators in the rewards store. Instead we show them in the merch tray for each product variant.
    isLowStock: hasCharacteristics ? false : isLowStock,
    hasStock: hasCharacteristics ? true : hasStock,
    isCollected,
    unlockLevel: cardUnlockLevel,
    redeemWithPoints,
    pointsCost,
    cashCost,
    onMerchCardClick: handleRewardMerchCardClick,
    coverPhotoBackgroundColor,
    addButtonText: hasCharacteristics
      ? t("rewards:viewCharacteristic", {
          characteristic: characteristics[0].name,
          context: isSmall ? "mobile" : undefined,
        })
      : t("common:add"),
    addButtonVariant,
  };

  switch (variant) {
    case DASHBOARD_CARD_VARIANT:
      return <DashboardMerchCard {...cardProps} />;
    case DEFAULT_CARD_VARIANT:
    default:
      return <MerchCard {...cardProps} />;
  }
};

export default MerchCardController;
